'use client';

import * as React from 'react';
import { LuRefreshCw, LuCircleAlert, LuWifiOff, LuShieldOff, LuServerOff } from 'react-icons/lu';
import type { IconType } from 'react-icons';

import { Box } from './box';
import { VStack } from './stack';
import { Text } from './text';
import { Button } from './button';

export type ErrorStateVariant = 'generic' | 'network' | 'permission' | 'server' | 'notFound';

export interface ErrorStateProps {
  /**
   * Error object or message to display
   */
  error?: Error | string | null;

  /**
   * Variant of error state
   * @default 'generic'
   */
  variant?: ErrorStateVariant;

  /**
   * Custom title (overrides variant default)
   */
  title?: string;

  /**
   * Custom description (overrides variant default)
   */
  description?: string;

  /**
   * Callback when retry button is clicked
   */
  onRetry?: () => void;

  /**
   * Custom retry button text
   * @default 'Try Again'
   */
  retryLabel?: string;

  /**
   * Whether retry is in progress
   */
  isRetrying?: boolean;

  /**
   * Custom icon component
   */
  icon?: React.ReactNode;

  /**
   * Icon size
   * @default 48
   */
  iconSize?: number;

  /**
   * Minimum height of the container
   * @default '300px'
   */
  minHeight?: string;

  /**
   * i18n translations
   */
  translations?: {
    generic?: { title?: string; description?: string };
    network?: { title?: string; description?: string };
    permission?: { title?: string; description?: string };
    server?: { title?: string; description?: string };
    notFound?: { title?: string; description?: string };
    retry?: string;
    retrying?: string;
  };
}

// Variant-specific icons
const variantIcons: Record<ErrorStateVariant, IconType> = {
  generic: LuCircleAlert,
  network: LuWifiOff,
  permission: LuShieldOff,
  server: LuServerOff,
  notFound: LuCircleAlert,
};

// Default translations
const defaultTranslations = {
  generic: {
    title: 'Something went wrong',
    description: 'An unexpected error occurred. Please try again.',
  },
  network: {
    title: 'Connection error',
    description: 'Unable to connect to the server. Please check your internet connection.',
  },
  permission: {
    title: 'Access denied',
    description: "You don't have permission to view this content.",
  },
  server: {
    title: 'Server error',
    description: 'The server encountered an error. Please try again later.',
  },
  notFound: {
    title: 'Not found',
    description: 'The requested resource could not be found.',
  },
  retry: 'Try Again',
  retrying: 'Retrying...',
};

/**
 * ErrorState - Standardized full-page error display component
 *
 * Use this component to display error states in pages consistently.
 * For inline errors (within forms/cards), use ErrorAlert instead.
 *
 * @example
 * ```tsx
 * const { isError, error, refetch, isRefetching } = useQuery(...);
 *
 * if (isError) {
 *   return (
 *     <ErrorState
 *       error={error}
 *       onRetry={refetch}
 *       isRetrying={isRefetching}
 *     />
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Network-specific error
 * <ErrorState
 *   variant="network"
 *   onRetry={() => window.location.reload()}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Custom error message
 * <ErrorState
 *   title="Failed to load subscription"
 *   description="We couldn't load your subscription details."
 *   onRetry={refetch}
 * />
 * ```
 */
export function ErrorState({
  error,
  variant = 'generic',
  title,
  description,
  onRetry,
  retryLabel,
  isRetrying = false,
  icon,
  iconSize = 48,
  minHeight = '300px',
  translations = {},
}: ErrorStateProps) {
  // Merge translations with defaults
  const t = {
    generic: { ...defaultTranslations.generic, ...translations.generic },
    network: { ...defaultTranslations.network, ...translations.network },
    permission: { ...defaultTranslations.permission, ...translations.permission },
    server: { ...defaultTranslations.server, ...translations.server },
    notFound: { ...defaultTranslations.notFound, ...translations.notFound },
    retry: translations.retry || defaultTranslations.retry,
    retrying: translations.retrying || defaultTranslations.retrying,
  };

  // Get variant content
  const variantContent = t[variant];
  const displayTitle = title || variantContent.title;
  const displayDescription =
    description ||
    (error ? (typeof error === 'string' ? error : error.message) : variantContent.description);

  // Get icon
  const IconComponent = variantIcons[variant];
  const displayIcon =
    icon ||
    React.createElement(IconComponent as React.ComponentType<{ size: number; color: string }>, {
      size: iconSize,
      color: '#EF4444',
    });

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight={minHeight}
      padding={6}
      width="100%"
      role="alert"
      aria-live="assertive"
    >
      <VStack gap={4} maxWidth="400px" textAlign="center">
        {/* Icon */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width={iconSize * 2}
          height={iconSize * 2}
          borderRadius="full"
          backgroundColor="#FEE2E2"
        >
          {displayIcon}
        </Box>

        {/* Text Content */}
        <VStack gap={2}>
          <Text fontSize="lg" fontWeight="semibold" color="#1F2937">
            {displayTitle}
          </Text>
          <Text fontSize="sm" color="#6B7280" maxWidth="300px">
            {displayDescription}
          </Text>
        </VStack>

        {/* Retry Button */}
        {onRetry && (
          <Button
            colorPalette="blue"
            variant="outline"
            onClick={onRetry}
            disabled={isRetrying}
            leftIcon={<LuRefreshCw size={16} />}
          >
            {isRetrying ? t.retrying : retryLabel || t.retry}
          </Button>
        )}
      </VStack>
    </Box>
  );
}

// Display name for debugging
ErrorState.displayName = 'ErrorState';
